/*
 * KDS Measurement Store — persistence abstraction.
 * The UI talks ONLY to KDSMeasure.Store. The storage provider is replaceable
 * without touching the UI. Contract (all async, return Promises):
 *   list() -> [profile]        get(id) -> profile|null
 *   save(profile) -> profile   remove(id) -> true
 *   getActiveId() -> id|null   setActiveId(id) -> id
 * profile = { id, name, updatedAt, readOnly, synced, data:{key:value}, uploads:[] }
 *
 * SOURCE OF TRUTH = the customer's Shopify account (customer.metafields.kds.*),
 * seeded read-only via Store.seedCanonical(). localStorage (TemporaryCacheProvider)
 * is an OFFLINE CACHE ONLY for unsynced working drafts — never canonical.
 * To go server-side: implement CustomerAccountProvider (Layer B) and call
 * KDSMeasure.Store.useProvider(new KDSMeasure.CustomerAccountProvider(cfg)); the UI is unchanged.
 */
(function () {
  if (window.KDSMeasure) return;

  var CACHE_PREFIX = 'kds_measure_cache_v2_';

  var FIELDS = [
    { k: 'length',        l: 'Length',       g: 'Upper', s: 'upper', num: true },
    { k: 'body_length',   l: 'Body Length',  g: 'Upper', s: 'upper', num: true },
    { k: 'shoulder',      l: 'Shoulder',     g: 'Upper', s: 'upper', num: true },
    { k: 'hand_length',   l: 'Hand Length',  g: 'Upper', s: 'upper', num: true },
    { k: 'hand_loose',    l: 'Hand Loose',   g: 'Upper', s: 'upper', num: true },
    { k: 'bicep',         l: 'Bicep',        g: 'Upper', s: 'upper', num: true },
    { k: 'armpit',        l: 'Armpit',       g: 'Upper', s: 'upper', num: true },
    { k: 'upper_chest',   l: 'Upper Chest',  g: 'Upper', s: 'upper', num: true },
    { k: 'chest',         l: 'Chest',        g: 'Upper', s: 'upper', num: true },
    { k: 'waist',         l: 'Waist',        g: 'Upper', s: 'upper', num: true },
    { k: 'hip_1',         l: 'Hip 1',        g: 'Upper', s: 'upper', num: true },
    { k: 'side_cut',      l: 'Side Cut',     g: 'Upper', s: 'upper', num: true },
    { k: 'hip_2',         l: 'Hip 2',        g: 'Upper', s: 'upper', num: true },
    { k: 'fn_down',       l: 'F.N.Down',     g: 'Neck',  s: 'upper', num: true, sub: 'Neck' },
    { k: 'bn_down',       l: 'B.N.Down',     g: 'Neck',  s: 'upper', num: true, sub: 'Neck' },
    { k: 'points',        l: 'Points',       g: 'Neck',  s: 'upper', num: true, sub: 'Neck' },
    { k: 'lower_length',  l: 'Length',       g: 'Lower', s: 'lower', num: true },
    { k: 'lower_waist',   l: 'Waist',        g: 'Lower', s: 'lower', num: true },
    { k: 'lower_hip',     l: 'Hip',          g: 'Lower', s: 'lower', num: true },
    { k: 'thigh_loose',   l: 'Thigh Loose',  g: 'Lower', s: 'lower', num: true },
    { k: 'knee_loose_1',  l: 'Knee Loose 1', g: 'Lower', s: 'lower', num: true },
    { k: 'knee_loose_2',  l: 'Knee Loose 2', g: 'Lower', s: 'lower', num: true },
    { k: 'bottom_loose',  l: 'Bottom Loose', g: 'Lower', s: 'lower', num: true },
    { k: 'round',         l: 'Round',        g: 'Lower', s: 'lower', num: true },
    { k: 'preferred_fit', l: 'Preferred Fit', g: 'Pref', s: 'prefs', num: false, opts: ['Fitted / Slim', 'True to size', 'Relaxed / Comfort', 'Flowy'] },
    { k: 'body_notes',    l: 'Body Notes',   g: 'Pref', s: 'notes', num: false, area: true },
    { k: 'special_instructions', l: 'Special Instructions', g: 'Pref', s: 'notes', num: false, area: true }
  ];

  function fieldsBy(s) { return FIELDS.filter(function (f) { return f.s === s; }); }
  function numericKeys() { return FIELDS.filter(function (f) { return f.num; }).map(function (f) { return f.k; }); }
  function nowISO() { return new Date().toISOString(); }
  function uid() { return 'p' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6); }
  function clone(o) { return JSON.parse(JSON.stringify(o)); }

  /* Escape untrusted strings before they are placed into innerHTML (profile names, etc.). */
  function esc(v) {
    return String(v == null ? '' : v)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function validate(data) {
    var errors = {};
    FIELDS.forEach(function (f) {
      if (!f.num) return;
      var v = data ? data[f.k] : '';
      if (v === '' || v == null) return;
      var n = Number(v);
      if (isNaN(n) || n <= 0) errors[f.k] = 'Enter a positive number';
      else if (n > 200) errors[f.k] = 'Too large';
    });
    return { ok: Object.keys(errors).length === 0, errors: errors };
  }

  function toLineItemProps(p) {
    var out = {};
    if (!p || !p.data) return out;
    FIELDS.forEach(function (f) {
      var v = p.data[f.k];
      if (v === '' || v == null) return;
      if (f.k === 'body_notes') return;
      if (f.num) out[f.g + ': ' + f.l] = v + ' in';
      else if (f.k === 'preferred_fit') out['Preferred Fit'] = v;
      else if (f.k === 'special_instructions') out['Special Instructions'] = v;
    });
    out['Measurement Profile'] = p.name || 'Profile';
    return out;
  }

  /* ---------- Provider: TemporaryCacheProvider (OFFLINE CACHE ONLY) ---------- */
  function TemporaryCacheProvider(scopeId) {
    this.name = 'temporary-cache';
    this.canonical = false;
    this._key = CACHE_PREFIX + (scopeId || 'guest');
    this._akey = this._key + '_active';
  }
  TemporaryCacheProvider.prototype._read = function () { try { return JSON.parse(localStorage.getItem(this._key) || '[]'); } catch (e) { return []; } };
  TemporaryCacheProvider.prototype._write = function (a) { try { localStorage.setItem(this._key, JSON.stringify(a)); } catch (e) {} };
  TemporaryCacheProvider.prototype.list = function () { return Promise.resolve(this._read()); };
  TemporaryCacheProvider.prototype.get = function (id) { var f = this._read().filter(function (p) { return p.id === id; })[0]; return Promise.resolve(f || null); };
  TemporaryCacheProvider.prototype.save = function (p) {
    var a = this._read();
    if (!p.id) p.id = uid();
    p.updatedAt = nowISO();
    p.synced = false;
    var i = -1; a.forEach(function (x, idx) { if (x.id === p.id) i = idx; });
    if (i >= 0) a[i] = p; else a.push(p);
    this._write(a);
    return Promise.resolve(p);
  };
  TemporaryCacheProvider.prototype.remove = function (id) { this._write(this._read().filter(function (p) { return p.id !== id; })); return Promise.resolve(true); };
  TemporaryCacheProvider.prototype.getActiveId = function () { try { return Promise.resolve(localStorage.getItem(this._akey)); } catch (e) { return Promise.resolve(null); } };
  TemporaryCacheProvider.prototype.setActiveId = function (id) { try { localStorage.setItem(this._akey, id); } catch (e) {} return Promise.resolve(id); };

  /* ---------- Provider: CustomerAccountProvider (LAYER B — scaffold) ----------
   * Same contract; drop-in replacement. Implement against a metaobject
   * definition `kds_measurement_profile` (fields = the 29 keys + name + updated_at)
   * written through the Customer Account API. Steps to enable:
   *   1) metaobjectDefinitionCreate type "kds_measurement_profile" with the fields.
   *   2) Expose it to the Customer Account API (access: storefront/customer).
   *   3) Implement the methods below (metaobjects query / metaobjectCreate /
   *      metaobjectUpdate / metaobjectDelete) and store active id in
   *      customer metafield kds.active_profile.
   *   4) KDSMeasure.Store.useProvider(new KDSMeasure.CustomerAccountProvider(cfg));
   * UI code does not change.
   */
  function CustomerAccountProvider(cfg) { this.name = 'customer-account'; this.canonical = true; this.cfg = cfg || {}; }
  function notEnabled() { return Promise.reject(new Error('LAYER_B_NOT_ENABLED')); }
  CustomerAccountProvider.prototype.list = function () { return notEnabled(); };
  CustomerAccountProvider.prototype.get = function () { return notEnabled(); };
  CustomerAccountProvider.prototype.save = function () { return notEnabled(); };
  CustomerAccountProvider.prototype.remove = function () { return notEnabled(); };
  CustomerAccountProvider.prototype.getActiveId = function () { return notEnabled(); };
  CustomerAccountProvider.prototype.setActiveId = function () { return notEnabled(); };

  /* ---------- Store facade (the only thing the UI touches) ---------- */
  var Store = {
    fields: FIELDS, fieldsBy: fieldsBy, numericKeys: numericKeys,
    validate: validate, toLineItemProps: toLineItemProps, esc: esc,
    _provider: null, _canonical: null,

    useProvider: function (p) { this._provider = p; return this; },
    provider: function () { return this._provider; },

    /* Canonical = read-only account record (source of truth). */
    seedCanonical: function (profile) {
      if (profile && profile.data) {
        var has = numericKeys().some(function (k) { return profile.data[k]; }) || profile.data.preferred_fit || profile.data.body_notes;
        if (has) {
          profile.id = 'account';
          profile.readOnly = true;
          profile.synced = true;
          this._canonical = profile;
        }
      }
      return this;
    },
    canonical: function () { return this._canonical; },

    list: function () {
      var self = this;
      return this._provider.list().then(function (arr) {
        var out = [];
        if (self._canonical) out.push(self._canonical);
        (arr || []).forEach(function (p) { if (p.id !== 'account') out.push(p); });
        return out;
      });
    },
    get: function (id) {
      if (id === 'account' && this._canonical) return Promise.resolve(this._canonical);
      return this._provider.get(id);
    },
    /* Writing the canonical forks a local draft so the account stays source of truth. */
    save: function (p) {
      if (p.id === 'account') { p = clone(p); p.id = null; p.readOnly = false; p.name = p.name || 'My Measurements'; }
      return this._provider.save(p);
    },
    remove: function (id) {
      if (id === 'account') return Promise.reject(new Error('CANONICAL_READONLY'));
      return this._provider.remove(id);
    },
    duplicate: function (id) {
      var self = this;
      return this.get(id).then(function (p) {
        if (!p) return null;
        var c = clone(p); c.id = null; c.readOnly = false; c.synced = false; c.name = (p.name || 'Profile') + ' (copy)';
        return self._provider.save(c);
      });
    },
    create: function (name) {
      return this._provider.save({ id: null, name: name || 'New Profile', readOnly: false, synced: false, data: {}, uploads: [] });
    },
    getActiveId: function () {
      var self = this;
      return this._provider.getActiveId().then(function (id) { return id || (self._canonical ? 'account' : null); });
    },
    setActiveId: function (id) { return this._provider.setActiveId(id); }
  };

  window.KDSMeasure = {
    Store: Store,
    FIELDS: FIELDS,
    TemporaryCacheProvider: TemporaryCacheProvider,
    CustomerAccountProvider: CustomerAccountProvider,
    version: 2
  };
})();
